import React from 'react';
import { AlertTriangle, ArrowLeft, Shield, XCircle } from 'lucide-react';
interface DisclaimersProps {
  onNavigate: (page: string) => void;
}
export function Disclaimers({
  onNavigate
}: DisclaimersProps) {
  return <div className="min-h-screen bg-white" data-id="element-386">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-387">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-388">
          <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-389">
            <ArrowLeft className="w-4 h-4" data-id="element-390" />
            <span className="text-sm font-medium" data-id="element-391">Back to Home</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-392">
        <div className="mb-8" data-id="element-393">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-394">
            Legal Disclaimers
          </h1>
          <p className="text-gray-600" data-id="element-395">Last Updated: January 27, 2026</p>
        </div>

        {/* Critical Warnings Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-396">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6" data-id="element-397">
            <div className="flex items-start gap-3 mb-3" data-id="element-398">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-399" />
              <h3 className="font-bold text-red-900 text-lg" data-id="element-400">
                NOT Legal Advice
              </h3>
            </div>
            <p className="text-red-800 text-sm" data-id="element-401">
              We are NOT attorneys. This is NOT legal advice. Consult a licensed
              attorney for legal guidance.
            </p>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6" data-id="element-402">
            <div className="flex items-start gap-3 mb-3" data-id="element-403">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" data-id="element-404" />
              <h3 className="font-bold text-yellow-900 text-lg" data-id="element-405">
                NOT HIPAA Compliant
              </h3>
            </div>
            <p className="text-yellow-800 text-sm" data-id="element-406">
              Data is NOT encrypted or HIPAA compliant. Do not enter highly
              sensitive medical information.
            </p>
          </div>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6" data-id="element-407">
            <div className="flex items-start gap-3 mb-3" data-id="element-408">
              <Shield className="w-6 h-6 text-orange-600 flex-shrink-0" data-id="element-409" />
              <h3 className="font-bold text-orange-900 text-lg" data-id="element-410">
                Beta Version Only
              </h3>
            </div>
            <p className="text-orange-800 text-sm" data-id="element-411">
              This is a testing prototype. No warranties. Data may be lost.
              Export backups regularly.
            </p>
          </div>

          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6" data-id="element-412">
            <div className="flex items-start gap-3 mb-3" data-id="element-413">
              <AlertTriangle className="w-6 h-6 text-purple-600 flex-shrink-0" data-id="element-414" />
              <h3 className="font-bold text-purple-900 text-lg" data-id="element-415">
                No Guarantees
              </h3>
            </div>
            <p className="text-purple-800 text-sm" data-id="element-416">
              No guarantee of admissibility in court. No guarantee of legal
              outcomes. Use at your own risk.
            </p>
          </div>
        </div>

        <div className="prose prose-gray max-w-none" data-id="element-417">
          {/* Disclaimer 1 */}
          <section className="mb-10" data-id="element-418">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-419">
              1. NOT LEGAL ADVICE - NO ATTORNEY-CLIENT RELATIONSHIP
            </h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-420">
              <p className="text-red-900 font-bold mb-3 text-lg" data-id="element-421">
                CRITICAL: READ THIS CAREFULLY
              </p>
              <div className="space-y-3 text-red-800" data-id="element-422">
                <p className="font-bold" data-id="element-423">
                  Zapiis is a documentation tool. We are NOT a law firm.
                </p>

                <p data-id="element-424">
                  <strong data-id="element-425">We are NOT attorneys</strong> and do NOT provide:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-426">
                  <li data-id="element-427">Legal advice of any kind</li>
                  <li data-id="element-428">Legal representation</li>
                  <li data-id="element-429">Legal services</li>
                  <li data-id="element-430">Legal opinions or recommendations</li>
                  <li data-id="element-431">Guidance on what legal actions to take or not take</li>
                </ul>

                <p className="mt-4" data-id="element-432">
                  <strong data-id="element-433">Using this Service does NOT create:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-434">
                  <li data-id="element-435">An attorney-client relationship</li>
                  <li data-id="element-436">Any confidential or privileged relationship</li>
                  <li data-id="element-437">Any duty of care or fiduciary duty</li>
                </ul>

                <p className="mt-4 font-bold" data-id="element-438">
                  NOTHING in this Service should be construed as legal advice or
                  used as a substitute for consultation with a licensed
                  attorney.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-439">
              What This Means for You:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-440">
              <li data-id="element-441">
                You MUST consult with a licensed attorney in your jurisdiction
                for legal advice specific to your situation
              </li>
              <li data-id="element-442">
                Do NOT rely on any information, suggestions, or AI-generated
                content from this Service as legal advice
              </li>
              <li data-id="element-443">
                Do NOT make legal decisions based solely on documentation
                created with this Service
              </li>
              <li data-id="element-444">
                We do NOT tell you what to do - we only provide a tool to
                organize information
              </li>
              <li data-id="element-445">
                We are NOT responsible for any legal outcomes or consequences of
                your actions
              </li>
            </ul>
          </section>

          {/* Disclaimer 2 */}
          <section className="mb-10" data-id="element-446">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-447">
              2. NOT HIPAA COMPLIANT - DATA SECURITY LIMITATIONS
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-4" data-id="element-448">
              <p className="text-yellow-900 font-bold mb-3 text-lg" data-id="element-449">
                BETA VERSION - LIMITED SECURITY
              </p>
              <div className="space-y-3 text-yellow-800" data-id="element-450">
                <p className="font-bold" data-id="element-451">
                  This beta version is NOT compliant with HIPAA (Health
                  Insurance Portability and Accountability Act).
                </p>

                <p data-id="element-452">
                  <strong data-id="element-453">Data Storage Limitations:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-454">
                  <li data-id="element-455">All data is stored in your browser's localStorage</li>
                  <li data-id="element-456">Data is NOT encrypted at rest</li>
                  <li data-id="element-457">Data is NOT encrypted to HIPAA standards</li>
                  <li data-id="element-458">Data is NOT backed up to secure cloud servers</li>
                  <li data-id="element-459">Data is NOT protected by enterprise-grade security</li>
                  <li data-id="element-460">
                    Clearing your browser cache will permanently delete all data
                  </li>
                </ul>

                <p className="mt-4 font-bold text-red-700" data-id="element-461">
                  DO NOT ENTER HIGHLY SENSITIVE INFORMATION including:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-red-700" data-id="element-462">
                  <li data-id="element-463">
                    Protected Health Information (PHI) requiring HIPAA
                    protection
                  </li>
                  <li data-id="element-464">Detailed medical records or diagnoses</li>
                  <li data-id="element-465">Social Security Numbers</li>
                  <li data-id="element-466">Financial account information</li>
                  <li data-id="element-467">Passwords or security credentials</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-468">
              Your Responsibilities:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-469">
              <li data-id="element-470">
                Regularly export backups of your data using the export
                functionality
              </li>
              <li data-id="element-471">
                Understand that data loss is possible and we cannot recover lost
                data
              </li>
              <li data-id="element-472">Protect your device and browser from unauthorized access</li>
              <li data-id="element-473">
                Do not use this Service for information requiring HIPAA-level
                protection
              </li>
              <li data-id="element-474">
                Wait for the full HIPAA-compliant version before entering
                sensitive medical information
              </li>
            </ul>
          </section>

          {/* Disclaimer 3 */}
          <section className="mb-10" data-id="element-475">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-476">
              3. NO WARRANTY - PROVIDED "AS IS"
            </h2>
            <div className="bg-gray-100 border-l-4 border-gray-500 p-6 mb-4" data-id="element-477">
              <p className="text-gray-900 font-bold mb-2" data-id="element-478">
                The Service is provided "AS IS" and "AS AVAILABLE" without
                warranties of any kind.
              </p>
              <p className="text-gray-700 text-sm" data-id="element-479">
                We make NO warranties, express or implied, including but not
                limited to warranties of merchantability, fitness for a
                particular purpose, title, or non-infringement.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-480">
              We Do NOT Warrant That:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-481">
              <li data-id="element-482">The Service will be uninterrupted, secure, or error-free</li>
              <li data-id="element-483">
                The Service will meet your specific requirements or expectations
              </li>
              <li data-id="element-484">
                Documentation created will be admissible as evidence in court
              </li>
              <li data-id="element-485">
                AI-generated content will be accurate, complete, or reliable
              </li>
              <li data-id="element-486">Your data will be preserved or recoverable if lost</li>
              <li data-id="element-487">The Service will result in any particular legal outcome</li>
            </ul>
          </section>

          {/* Disclaimer 4 */}
          <section className="mb-10" data-id="element-488">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-489">
              4. NO GUARANTEE OF ADMISSIBILITY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-490">
              <strong data-id="element-491">
                We make NO representations or warranties that documentation
                created using this Service will be admissible as evidence in any
                legal proceeding.
              </strong>
            </p>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-492">
              Admissibility of evidence is determined by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-493">
              <li data-id="element-494">Courts based on applicable rules of evidence</li>
              <li data-id="element-495">Jurisdiction-specific laws and procedures</li>
              <li data-id="element-496">The discretion of judges and legal standards</li>
              <li data-id="element-497">Proper authentication and chain of custody requirements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-498">
              <strong data-id="element-499">You MUST consult with an attorney</strong> regarding
              evidence requirements, admissibility standards, and proper
              documentation procedures for your specific case and jurisdiction.
            </p>
          </section>

          {/* Disclaimer 5 */}
          <section className="mb-10" data-id="element-500">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-501">
              5. AI-GENERATED CONTENT DISCLAIMER
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-502">
              <p className="text-blue-900 font-bold mb-2" data-id="element-503">
                Artificial Intelligence Limitations
              </p>
              <p className="text-blue-800 text-sm" data-id="element-504">
                Features using AI (case summaries, attorney search) may produce
                inaccurate, incomplete, or misleading information. Always verify
                AI-generated content independently.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-505">
              AI Content Warnings:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-506">
              <li data-id="element-507">
                AI-generated case summaries may contain errors or omissions
              </li>
              <li data-id="element-508">
                Attorney recommendations are NOT endorsements and should be
                independently verified
              </li>
              <li data-id="element-509">
                AI cannot provide legal advice or professional recommendations
              </li>
              <li data-id="element-510">
                AI may misinterpret your information or provide irrelevant
                suggestions
              </li>
              <li data-id="element-511">
                You are responsible for reviewing and verifying all AI-generated
                content
              </li>
              <li data-id="element-512">
                Do NOT rely solely on AI-generated content for legal decisions
              </li>
            </ul>
          </section>

          {/* Disclaimer 6 */}
          <section className="mb-10" data-id="element-513">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-514">
              6. LIMITATION OF LIABILITY
            </h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-515">
              <p className="text-red-900 font-bold mb-2" data-id="element-516">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <p className="text-red-800 text-sm" data-id="element-517">
                Zapiis and its affiliates, employees, contractors, and partners
                SHALL NOT BE LIABLE for any damages arising from your use or
                inability to use the Service.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-518">
              We Are NOT Liable For:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-519">
              <li data-id="element-520">Loss of data, revenue, profits, or business opportunities</li>
              <li data-id="element-521">
                Legal outcomes or consequences of actions taken based on
                documentation created with the Service
              </li>
              <li data-id="element-522">
                Damages resulting from reliance on any information provided by
                the Service
              </li>
              <li data-id="element-523">
                Indirect, incidental, special, consequential, or punitive
                damages
              </li>
              <li data-id="element-524">
                Damages from data breaches, unauthorized access, or security
                incidents
              </li>
              <li data-id="element-525">Damages from errors, bugs, or technical failures</li>
              <li data-id="element-526">Damages from AI-generated content or recommendations</li>
            </ul>
          </section>

          {/* Disclaimer 7 */}
          <section className="mb-10" data-id="element-527">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-528">
              7. NO PROFESSIONAL RECOMMENDATIONS
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-529">
              <strong data-id="element-530">We do NOT provide recommendations on:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-531">
              <li data-id="element-532">What legal actions you should or should not take</li>
              <li data-id="element-533">Whether you have a valid legal case</li>
              <li data-id="element-534">Which attorney you should hire</li>
              <li data-id="element-535">How to present your case or evidence</li>
              <li data-id="element-536">What information to include or exclude from documentation</li>
              <li data-id="element-537">Legal strategy or tactics</li>
              <li data-id="element-538">Settlement decisions or negotiations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-539">
              All decisions regarding your legal matters are yours alone to
              make, preferably in consultation with a licensed attorney.
            </p>
          </section>

          {/* Disclaimer 8 */}
          <section className="mb-10" data-id="element-540">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-541">
              8. BETA VERSION - TESTING PURPOSES ONLY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-542">
              This is a <strong data-id="element-543">beta testing version</strong> of the Service.
              Beta versions are:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-544">
              <li data-id="element-545">Incomplete and subject to significant changes</li>
              <li data-id="element-546">Not fully tested and may contain bugs or errors</li>
              <li data-id="element-547">Provided for testing and feedback purposes only</li>
              <li data-id="element-548">
                Not intended for production use with real, sensitive cases
              </li>
              <li data-id="element-549">
                Subject to termination or modification at any time without
                notice
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-550">
              <strong data-id="element-551">Use this beta version at your own risk.</strong> Wait for
              the full release with HIPAA compliance and enterprise security
              before documenting highly sensitive cases.
            </p>
          </section>

          {/* Disclaimer 9 */}
          <section className="mb-10" data-id="element-552">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-553">
              9. USER RESPONSIBILITY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-554">
              <strong data-id="element-555">You are solely responsible for:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-556">
              <li data-id="element-557">
                The accuracy and completeness of all information you enter
              </li>
              <li data-id="element-558">
                Determining whether this Service is appropriate for your needs
              </li>
              <li data-id="element-559">
                Consulting with qualified legal counsel before taking legal
                action
              </li>
              <li data-id="element-560">Verifying all AI-generated content and recommendations</li>
              <li data-id="element-561">Maintaining backups of your data</li>
              <li data-id="element-562">Protecting your device and preventing unauthorized access</li>
              <li data-id="element-563">
                Complying with all applicable laws in your use of the Service
              </li>
              <li data-id="element-564">Any consequences resulting from your use of the Service</li>
            </ul>
          </section>

          {/* Disclaimer 10 */}
          <section className="mb-10" data-id="element-565">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-566">
              10. CHANGES AND UPDATES
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-567">
              We reserve the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-568">
              <li data-id="element-569">
                Modify, suspend, or discontinue the Service at any time without
                notice
              </li>
              <li data-id="element-570">Update these disclaimers at any time</li>
              <li data-id="element-571">Change features, functionality, or pricing without notice</li>
              <li data-id="element-572">Terminate beta access for any reason</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-573">
              Continued use of the Service after changes constitutes acceptance
              of the modified disclaimers.
            </p>
          </section>

          {/* Final Acknowledgment */}
          <div className="bg-gray-900 text-white rounded-lg p-8 mt-12" data-id="element-574">
            <h3 className="text-xl font-bold mb-4" data-id="element-575">
              BY USING THIS SERVICE, YOU ACKNOWLEDGE AND AGREE THAT:
            </h3>
            <ul className="space-y-3 text-sm" data-id="element-576">
              <li className="flex items-start gap-2" data-id="element-577">
                <span className="text-yellow-400 font-bold" data-id="element-578">✓</span>
                <span data-id="element-579">
                  You have read and understood all disclaimers on this page
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-580">
                <span className="text-yellow-400 font-bold" data-id="element-581">✓</span>
                <span data-id="element-582">
                  You understand this is NOT legal advice and we are NOT
                  attorneys
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-583">
                <span className="text-yellow-400 font-bold" data-id="element-584">✓</span>
                <span data-id="element-585">
                  You understand data is NOT HIPAA compliant or encrypted
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-586">
                <span className="text-yellow-400 font-bold" data-id="element-587">✓</span>
                <span data-id="element-588">
                  You will consult with a licensed attorney for legal guidance
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-589">
                <span className="text-yellow-400 font-bold" data-id="element-590">✓</span>
                <span data-id="element-591">
                  You accept all risks associated with using this beta Service
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-592">
                <span className="text-yellow-400 font-bold" data-id="element-593">✓</span>
                <span data-id="element-594">
                  You will not hold Zapiis liable for any damages or legal
                  outcomes
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-595">
                <span className="text-yellow-400 font-bold" data-id="element-596">✓</span>
                <span data-id="element-597">You will regularly export backups of your data</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="mt-12 text-center" data-id="element-598">
            <p className="text-gray-600 mb-2" data-id="element-599">
              Questions about these disclaimers?
            </p>
            <p className="text-gray-900 font-medium" data-id="element-600">
              Contact: [Your Legal Contact Email]
            </p>
          </div>
        </div>
      </main>
    </div>;
}